import React, { useEffect, useState } from 'react';
import { SIGNUP_STEPS, SIGNUP_INITIAL_STATE } from '../../constants/signup';

const SIGNUP_STEP_META = {
  contact: {
    subtitle: 'Queremos saber cómo contactarte y darte la bienvenida personalizada.',
  },
  avatar: {
    subtitle: 'Una imagen fresca ayuda a los coaches a reconocerte en el estudio.',
  },
  access: {
    subtitle: 'Protegemos tu acceso con credenciales seguras y fáciles de recordar.',
  },
};

function LoginScreen({ onLogin, theme }) {
  const [mode, setMode] = useState('login');
  const [signupStep, setSignupStep] = useState(0);
  const [signupData, setSignupData] = useState(SIGNUP_INITIAL_STATE);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState({ message: '', field: null });
  const [showPassword, setShowPassword] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoveryError, setRecoveryError] = useState('');
  const [recoverySuccess, setRecoverySuccess] = useState(false);
  const [contactErrors, setContactErrors] = useState({ name: '', phone: '' });
  const [avatarError, setAvatarError] = useState('');
  const [accessErrors, setAccessErrors] = useState({ email: '', password: '' });
  const [showSignupModal, setShowSignupModal] = useState(false);

  useEffect(() => {
    return () => {
      if (signupData.avatarPreview) {
        URL.revokeObjectURL(signupData.avatarPreview);
      }
    };
  }, [signupData.avatarPreview]);

  const isSignup = mode === 'signup';
  const isRecovery = mode === 'recovery';
  const currentStep = SIGNUP_STEPS[signupStep];
  const isLastStep = signupStep === SIGNUP_STEPS.length - 1;

  const resetSignupFlow = () => {
    setSignupStep(0);
    setSignupData((prev) => {
      if (prev.avatarPreview) {
        URL.revokeObjectURL(prev.avatarPreview);
      }
      return SIGNUP_INITIAL_STATE;
    });
    setContactErrors({ name: '', phone: '' });
    setAvatarError('');
    setAccessErrors({ email: '', password: '' });
    setShowSignupModal(false);
  };

  const openSignup = () => {
    resetSignupFlow();
    setMode('signup');
    setLoginError({ message: '', field: null });
    setShowPassword(false);
  };

  const returnToLogin = () => {
    resetSignupFlow();
    setMode('login');
    setLoginError({ message: '', field: null });
    setRecoveryEmail('');
    setRecoveryError('');
    setRecoverySuccess(false);
    setShowPassword(false);
  };

  const openRecovery = () => {
    resetSignupFlow();
    setMode('recovery');
    setLoginError({ message: '', field: null });
    setRecoveryError('');
    setRecoverySuccess(false);
    setShowPassword(false);
  };

  const handleAvatarChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    const preview = URL.createObjectURL(file);
    setSignupData((prev) => {
      if (prev.avatarPreview) {
        URL.revokeObjectURL(prev.avatarPreview);
      }
      return {
        ...prev,
        avatar: file,
        avatarPreview: preview,
      };
    });
    setAvatarError('');
  };

  const handleFieldChange = (field) => (event) => {
    const value = event.target.value;
    setSignupData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (field === 'name' || field === 'phone') {
      setContactErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
    if (field === 'email' || field === 'password') {
      setAccessErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleLoginFieldChange = (field) => (event) => {
    const value = event.target.value;
    setLoginData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setLoginError({ message: '', field: null });
  };

  const canSubmitLogin = loginData.email.trim().length > 0 && loginData.password.trim().length > 0;

  const handleRecoverySubmit = (event) => {
    event.preventDefault();
    const emailValue = recoveryEmail.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailValue) {
      setRecoveryError('Ingresa tu correo para recibir el enlace.');
      setRecoverySuccess(false);
      return;
    }

    if (!emailRegex.test(emailValue)) {
      setRecoveryError('Ingresa un correo válido.');
      setRecoverySuccess(false);
      return;
    }

    setRecoveryError('');
    setRecoverySuccess(true);
  };

  const handleLoginSubmit = (event) => {
    event.preventDefault();
    // Validation bypassed for easy access as requested
    setLoginError({ message: '', field: null });
    onLogin();
  };

  const canContinue = () => {
    if (currentStep.id === 'contact') {
      const nameValid = signupData.name.trim().length >= 2;
      const phoneValue = signupData.phone.replace(/\D/g, '');
      const phoneValid = phoneValue.length >= 8;
      return nameValid && phoneValid;
    }
    if (currentStep.id === 'access') {
      const emailValue = signupData.email.trim();
      const passwordValue = signupData.password.trim();
      return /.+@.+\..+/.test(emailValue) && passwordValue.length >= 6;
    }
    return true;
  };

  const goNextStep = () => {
    if (currentStep.id === 'contact') {
      const trimmedName = signupData.name.trim();
      const phoneDigits = signupData.phone.replace(/\D/g, '');
      const nextErrors = {
        name: trimmedName.length >= 2 ? '' : 'Ingresa tu nombre.',
        phone: phoneDigits.length >= 8 ? '' : 'Ingresa un teléfono válido.',
      };

      const hasErrors = nextErrors.name || nextErrors.phone;
      setContactErrors(nextErrors);
      if (hasErrors) {
        return;
      }
    }

    if (currentStep.id === 'avatar') {
      if (!signupData.avatar && !signupData.avatarPreview) {
        setAvatarError('Selecciona una foto para continuar.');
        return;
      }
      setAvatarError('');
    }

    if (currentStep.id === 'access') {
      const emailValue = signupData.email.trim();
      const passwordValue = signupData.password.trim();
      const nextErrors = {
        email: /.+@.+\..+/.test(emailValue) ? '' : 'Ingresa un correo válido.',
        password: passwordValue.length >= 6 ? '' : 'Ingresa una contraseña de al menos 6 caracteres.',
      };

      const hasErrors = nextErrors.email || nextErrors.password;
      setAccessErrors(nextErrors);
      if (hasErrors) {
        return;
      }

      setShowSignupModal(true);
      return;
    }

    if (!canContinue()) return;
    if (isLastStep) {
      onLogin();
      return;
    }
    setSignupStep((prev) => Math.min(prev + 1, SIGNUP_STEPS.length - 1));
  };

  const handleCompleteSignup = () => {
    const profileOverrides = {
      name: signupData.name.trim(),
      phone: signupData.phone.trim(),
      email: signupData.email.trim(),
      avatarFile: signupData.avatar,
      avatarUrl: signupData.avatarPreview,
      passwordProvided: Boolean(signupData.password.trim()),
    };

    setShowSignupModal(false);
    onLogin(profileOverrides);
    resetSignupFlow();
  };

  const goPrevStep = () => {
    setSignupStep((prev) => Math.max(prev - 1, 0));
  };

  // --- Render (New Game Start Design) ---
  return (
    <div className="login-mesh-bg" data-theme={theme}>
      {/* Floating Glass Card */}
      <div className="login-glass-card">

        {/* Header / Logo */}
        <div className="login-hero-logo">
          <img src="/logo-sportivos.png" alt="Sportivos" />

        </div>

        {/* 1. Recovery Mode */}
        {isRecovery && (
          <div className="animate-fade-in" style={{ animation: 'fadeIn 0.5s ease' }}>
            <h2 style={{ color: '#fff', fontWeight: 700, fontSize: '1.4rem', marginBottom: '0.5rem', textAlign: 'center' }}>Recuperar acceso</h2>
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem', textAlign: 'center', marginBottom: '1.5rem' }}>
              Te enviaremos un enlace mágico
            </p>

            <form onSubmit={handleRecoverySubmit} style={{ display: 'grid', gap: '1.2rem' }}>
              <div className="glass-input-group">
                <input
                  className="glass-input"
                  type="email"
                  value={recoveryEmail}
                  onChange={(e) => { setRecoveryEmail(e.target.value); setRecoveryError(''); }}
                  placeholder="Correo electrónico"
                />
              </div>
              {recoveryError && <p style={{ color: '#ef4444', fontSize: '0.85rem' }}>{recoveryError}</p>}

              <button type="submit" className="login-btn-primary">
                Enviar enlace
              </button>
            </form>

            <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
              <button className="login-link" onClick={returnToLogin}>
                Volver a iniciar sesión
              </button>
            </div>
          </div>
        )}

        {/* 2. Signup Mode (Wizard) */}
        {isSignup && (
          <div className="animate-fade-in" style={{ animation: 'fadeIn 0.5s ease' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
              <button className="login-link" onClick={signupStep === 0 ? returnToLogin : goPrevStep}>← Atrás</button>
              <span className="badge badge-outline">Paso {signupStep + 1}/{SIGNUP_STEPS.length}</span>
            </div>

            <h2 style={{ color: '#fff', fontWeight: 700, fontSize: '1.3rem', marginBottom: '0.2rem' }}>
              {currentStep.title}
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
              {SIGNUP_STEP_META[currentStep.id]?.subtitle}
            </p>

            {/* Wizard Content */}
            <div style={{ display: 'grid', gap: '1rem' }}>
              {currentStep.id === 'contact' && (
                <>
                  <input className="glass-input" placeholder="Tu nombre y apellido" value={signupData.name} onChange={handleFieldChange('name')} />
                  <input className="glass-input" placeholder="+56 9 1234 5678" type="tel" value={signupData.phone} onChange={handleFieldChange('phone')} />
                </>
              )}
              {currentStep.id === 'avatar' && (
                <div style={{ textAlign: 'center', padding: '1rem', border: '1px dashed rgba(255,255,255,0.2)', borderRadius: '12px' }}>
                  {signupData.avatarPreview ? (
                    <div style={{ width: '80px', height: '80px', margin: '0 auto', borderRadius: '50%', overflow: 'hidden' }}>
                      <img src={signupData.avatarPreview} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="Preview" />
                    </div>
                  ) : (
                    <div style={{ fontSize: '2rem' }}>📷</div>
                  )}
                  <label className="login-link" style={{ display: 'block', marginTop: '1rem', cursor: 'pointer' }}>
                    Elegir foto
                    <input type="file" hidden accept="image/*" onChange={handleAvatarChange} />
                  </label>
                </div>
              )}
              {currentStep.id === 'access' && (
                <>
                  <input className="glass-input" type="email" placeholder="Correo electrónico" value={signupData.email} onChange={handleFieldChange('email')} />
                  <input className="glass-input" type="password" placeholder="Contraseña segura" value={signupData.password} onChange={handleFieldChange('password')} />
                </>
              )}
            </div>

            <div style={{ marginTop: '2rem' }}>
              <button
                className="login-btn-primary"
                onClick={goNextStep}
                disabled={['contact', 'avatar', 'access'].includes(currentStep.id) ? false : !canContinue()}
              >
                {isLastStep ? 'Crear Cuenta' : 'Siguiente'}
              </button>
            </div>
          </div>
        )}

        {/* 3. Login Mode (Default) */}
        {!isRecovery && !isSignup && (
          <div className="animate-fade-in" style={{ animation: 'fadeIn 0.5s ease' }}>
            <form onSubmit={handleLoginSubmit} style={{ display: 'grid', gap: '1.2rem' }}>
              <div className="glass-input-group">
                <input
                  id="login-email"
                  type="email"
                  className="glass-input"
                  value={loginData.email}
                  onChange={handleLoginFieldChange('email')}
                  placeholder="Correo electrónico"
                  autoComplete="email"
                />
              </div>

              <div className="glass-input-group">
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  className="glass-input"
                  value={loginData.password}
                  onChange={handleLoginFieldChange('password')}
                  placeholder="Contraseña"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer' }}
                >
                  {showPassword ? '🐵' : '🙈'}
                </button>
              </div>

              {loginError.message && (
                <div style={{ padding: '0.8rem', background: 'rgba(239, 68, 68, 0.15)', borderRadius: '12px', color: '#fca5a5', fontSize: '0.85rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <span>⚠️</span> {loginError.message}
                </div>
              )}

              <button type="submit" className="login-btn-primary">
                Iniciar Sesión
              </button>
            </form>

            <div className="login-footer-links" style={{ marginTop: '1.5rem' }}>
              <button className="login-link" onClick={openSignup}>
                Crear cuenta
              </button>
              <button className="login-link" onClick={openRecovery}>
                Olvidé mi contraseña
              </button>
            </div>
          </div>
        )}

      </div>

      {/* Modal for Signup Success */}
      {showSignupModal && (
        <div className="auth-modal" role="dialog" aria-modal="true" style={{ zIndex: 2000 }}>
          <div className="login-glass-card" style={{ animation: 'none', textAlign: 'center' }}>
            <h2 style={{ color: '#4ade80', fontSize: '2rem' }}>¡Listo! 🎉</h2>
            <p style={{ color: '#ccc' }}>Tu perfil de atleta ha sido creado.</p>
            <button type="button" className="login-btn-primary" onClick={handleCompleteSignup}>
              Entrar al Gym
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default LoginScreen;
